'use client';

import { useParams } from 'next/navigation';
import React from 'react';

import { doTranslation } from '@/app/i18n/client';
import { localeSpecificPath } from '@/utils/localeUtils';

import styles from '@/components/home-page/home-page-top-destinations.module.scss';

interface Props {
  lng: string;
}

const HomePageTopDestinations = (props: Props): React.JSX.Element => {
  const { lng }: any = useParams();
  const { t } = doTranslation(lng);
  let topDestinations = [
    {
      title: `${t('Bus Tickets to')} New York`,
      id: 'to-new-york',
      options: [
        {
          name: ['New Paltz, NY', 'New York, NY'],
          url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
            '/schedules/bus-from-new-paltz-ny-to-new-york-ny/',
            lng
          )}`
        },
        {
          name: ['Kingston, NY', 'New York, NY'],
          url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
            '/schedules/bus-from-kingston-ny-to-new-york-ny/',
            lng
          )}`
        },
        {
          name: ['Montreal, QC', 'New York, NY'],
          url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
            '/schedules/bus-from-montreal-qc-to-new-york-ny/',
            lng
          )}`
        },
        {
          name: ['Albany, NY', 'New York, NY'],
          url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
            '/schedules/bus-from-albany-ny-to-new-york-ny/',
            lng
          )}`
        },
        {
          name: ['Rosendale, NY', 'New York, NY'],
          url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
            '/schedules/bus-from-rosendale-ny-to-new-york-ny/',
            lng
          )}`
        },
        {
          name: ['Oneonta, NY', 'New York, NY'],
          url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
            '/schedules/bus-from-oneonta-ny-to-new-york-ny/',
            lng
          )}`
        },
        {
          name: ['Syracuse, NY', 'New York, NY'],
          url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
            '/schedules/bus-from-syracuse-ny-to-new-york-ny/',
            lng
          )}`
        },
        {
          name: ['Toronto, ON', 'New York, NY'],
          url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
            '/schedules/bus-from-toronto-on-to-new-york-ny/',
            lng
          )}`
        },
        {
          name: ['Binghamton, NY', 'New York, NY'],
          url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
            '/schedules/bus-from-binghamton-ny-to-new-york-ny/',
            lng
          )}`
        }
      ]
    },
    {
      title: `${t('Bus Tickets to')} Montreal`,
      id: 'to-montreal',
      options: [
        {
          name: ['New York, NY', 'Montreal, QC'],
          url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
            '/schedules/bus-from-new-york-ny-to-montreal-qc/',
            lng
          )}`
        },
        {
          name: ['Albany, NY', 'Montreal, QC'],
          url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
            '/schedules/bus-from-albany-ny-to-montreal-qc/',
            lng
          )}`
        },
        {
          name: ['Toronto, ON', 'Montreal, QC'],
          url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
            '/schedules/bus-from-toronto-on-to-montreal-qc/',
            lng
          )}`
        },
        {
          name: ['Boston, MA', 'Montreal, QC'],
          url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
            '/schedules/bus-from-boston-ma-to-montreal-qc/',
            lng
          )}`
        },
        {
          name: ['Plattsburgh, NY', 'Montreal, QC'],
          url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
            '/schedules/bus-from-plattsburgh-ny-to-montreal-qc/',
            lng
          )}`
        },
        {
          name: ['Saratoga Springs, NY', 'Montreal, QC'],
          url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
            '/schedules/bus-from-saratoga-springs-ny-to-montreal-qc/',
            lng
          )}`
        },
        {
          name: ['Ridgewood, NJ', 'Montreal, QC'],
          url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
            '/schedules/bus-from-ridgewood-nj-to-montreal-qc/',
            lng
          )}`
        },
        {
          name: ['Hamilton, ON', 'Montreal, QC'],
          url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
            '/schedules/bus-from-hamilton-on-to-montreal-qc/',
            lng
          )}`
        },
        {
          name: ['Potsdam, NY', 'Montreal, QC'],
          url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
            '/schedules/bus-from-potsdam-ny-to-montreal-qc/',
            lng
          )}`
        }
      ]
    },
    {
      title: `${t('Bus Tickets to')} Chicago`,
      id: 'to-chicago',
      options: [
        {
          name: ['Iowa City, IA', 'Chicago, IL'],
          url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
            '/schedules/bus-from-iowa-city-ia-to-chicago-il/',
            lng
          )}`
        },
        {
          name: ['Des Moines, IA', 'Chicago, IL'],
          url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
            '/schedules/bus-from-des-moines-ia-to-chicago-il/',
            lng
          )}`
        },
        {
          name: ['Cedar Rapids, IA', 'Chicago, IL'],
          url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
            '/schedules/bus-from-cedar-rapids-ia-to-chicago-il/',
            lng
          )}`
        },
        {
          name: ['Dubuque, IA', 'Chicago, IL'],
          url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
            '/schedules/bus-from-dubuque-ia-to-chicago-il/',
            lng
          )}`
        },
        {
          name: ['Waterloo, IA', 'Chicago, IL'],
          url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
            '/schedules/bus-from-waterloo-ia-to-chicago-il/',
            lng
          )}`
        },
        {
          name: ['Peoria, IL', 'Chicago, IL'],
          url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
            '/schedules/bus-from-peoria-il-to-chicago-il/',
            lng
          )}`
        },
        {
          name: ['Omaha, NE', 'Chicago, IL'],
          url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
            '/schedules/bus-from-omaha-ne-to-chicago-il/',
            lng
          )}`
        },
        {
          name: ['Champaign, IL', 'Chicago, IL'],
          url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
            '/schedules/bus-from-champaign-il-to-chicago-il/',
            lng
          )}`
        },
        {
          name: ['Indianapolis, IN', 'Chicago, IL'],
          url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
            '/schedules/bus-from-indianapolis-in-to-chicago-il/',
            lng
          )}`
        }
      ]
    },
    {
      title: `${t('Bus Tickets to')} Boston`,
      id: 'to-boston',
      options: [
        {
          name: ['Albany, NY', 'Boston, MA'],
          url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
            '/schedules/bus-from-albany-ny-to-boston-ma/',
            lng
          )}`
        },
        {
          name: ['New York, NY', 'Boston, MA'],
          url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
            '/schedules/bus-from-new-york-ny-to-boston-ma/',
            lng
          )}`
        },
        {
          name: ['Concord, NH', 'Boston, MA'],
          url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
            '/schedules/bus-from-concord-nh-to-boston-ma/',
            lng
          )}`
        },
        {
          name: ['Provincetown, MA', 'Boston, MA'],
          url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
            '/schedules/bus-from-provincetown-ma-to-boston-ma/',
            lng
          )}`
        },
        {
          name: ['Burlington, VT', 'Boston, MA'],
          url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
            '/schedules/bus-from-burlington-vt-to-boston-ma/',
            lng
          )}`
        },
        {
          name: ['Portland, ME', 'Boston, MA'],
          url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
            '/schedules/bus-from-portland-me-to-boston-ma/',
            lng
          )}`
        },
        {
          name: ['Amherst, MA', 'Boston, MA'],
          url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
            '/schedules/bus-from-amherst-ma-to-boston-ma/',
            lng
          )}`
        },
        {
          name: ['New Paltz, NY', 'Boston, MA'],
          url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
            '/schedules/bus-from-new-paltz-ny-to-boston-ma/',
            lng
          )}`
        },
        {
          name: ['Williamstown, MA', 'Boston, MA'],
          url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
            '/schedules/bus-from-williamstown-ma-to-boston-ma/',
            lng
          )}`
        }
      ]
    }
  ];

  if (process.env.NEXT_PUBLIC_COUNTRY == 'ca') {
    topDestinations = [
      {
        title: `${t('Bus Tickets to')} New York`,
        id: 'to-new-york',
        options: [
          {
            name: ['Mississauga, ON', 'New York, NY'],
            url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
              '/schedules/bus-from-mississauga-on-to-new-york-ny/',
              lng
            )}`
          },
          {
            name: ['Buffalo, NY', 'New York, NY'],
            url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
              '/schedules/bus-from-buffalo-ny-to-new-york-ny/',
              lng
            )}`
          },
          {
            name: ['Massena, NY', 'New York, NY'],
            url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
              '/schedules/bus-from-massena-ny-to-new-york-ny/',
              lng
            )}`
          }
        ]
      },
      {
        title: `${t('Bus Tickets to')} Montreal`,
        id: 'to-montreal',
        options: [
          {
            name: ['Albany, NY', 'Montreal, QC'],
            url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
              '/schedules/bus-from-albany-ny-to-montreal-qc/',
              lng
            )}`
          },
          {
            name: ['Plattsburgh, NY', 'Montreal, QC'],
            url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
              '/schedules/bus-from-plattsburgh-ny-to-montreal-qc/',
              lng
            )}`
          },
          {
            name: ['Toronto, ON', 'Montreal, QC'],
            url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
              '/schedules/bus-from-toronto-on-to-montreal-qc/',
              lng
            )}`
          }
        ]
      },
      {
        title: `${t('Bus Tickets to')} Toronto`,
        id: 'to-toronto',
        options: [
          {
            name: ['New York, NY', 'Toronto, ON'],
            url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
              '/schedules/bus-from-new-york-ny-to-toronto-on/',
              lng
            )}`
          },
          {
            name: ['Windsor, ON', 'Toronto, ON'],
            url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
              '/schedules/bus-from-windsor-on-to-toronto-on/',
              lng
            )}`
          },
          {
            name: ['London, ON', 'Toronto, ON'],
            url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
              '/schedules/bus-from-london-on-to-toronto-on/',
              lng
            )}`
          }
        ]
      },
      {
        title: `${t('Bus Tickets to')} Detroit`,
        id: 'to-detroit',
        options: [
          {
            name: ['Toronto, ON', 'Detroit, MI'],
            url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
              '/schedules/bus-from-toronto-on-to-detroit-mi/',
              lng
            )}`
          },
          {
            name: ['London, ON', 'Detroit, MI'],
            url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
              '/schedules/bus-from-london-on-to-detroit-mi/',
              lng
            )}`
          },
          {
            name: ['Mississauga, ON', 'Detroit, MI'],
            url: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${localeSpecificPath(
              '/schedules/bus-from-mississauga-on-to-detroit-mi/',
              lng
            )}`
          }
        ]
      }
    ];
  }

  return (
    <div className={styles['top-destinations']}>
      <div className={styles.header}>
        <h2 className={styles.title}>{t('Secure Your Seat to Our Top Destinations With Just A Few Clicks')}</h2>
        <p className={styles.subtitle}>{t('Book your bus ticket today')}</p>
      </div>
      <div className={styles.block}>
        <div className={styles.content}>
          {topDestinations.map((destination: any, i: number) => {
            return (
              <div className={styles.col} key={i}>
                <h3 className={styles.title} id={destination.id}>
                  {destination.title}
                </h3>
                <ul aria-labelledby={destination.id} className={styles.options}>
                  {destination.options.map((option: any, j: number) => {
                    return (
                      <li key={j}>
                        <a className={`${styles.text} gtm-top_destinations`} href={option.url}>
                          {option.name[0]} <span className={styles.to}>{t('to')}</span> {option.name[1]}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default HomePageTopDestinations;
